import { useCallback, useEffect, useState } from 'react'
import { api, jsonBody } from '../api'
import { userLabel, type User } from '../types'
import type { Locale } from '../i18n'

export function PendingAccess({ user, locale, reload, logout, error }: { user: User; locale: Locale; reload: () => Promise<void>; logout: () => Promise<void>; error: string }) {
  const rejected = user.accessStatus === 'rejected'
  const zh = locale === 'zh-CN'
  useEffect(() => {
    if (rejected) return
    let pending = false
    const timer = setInterval(() => {
      if (pending || document.visibilityState === 'hidden') return
      pending = true
      void reload().finally(() => { pending = false })
    }, 5000)
    return () => clearInterval(timer)
  }, [rejected, reload])
  return <div className="load-page"><section className="content-section" style={{ maxWidth: 560, textAlign: 'center' }}>
    <h1>{rejected ? (zh ? '接入申请未通过' : 'Access request declined') : (zh ? '等待管理员审批' : 'Awaiting administrator approval')}</h1>
    <p>{userLabel(user)}</p>
    <p>{rejected ? (zh ? '你的接入申请未获批准。如需帮助，请联系平台管理员。' : 'Your access request was declined. Contact the platform administrator for assistance.') : (zh ? '你已成功登录。当前平台采用审批制，管理员通过后会自动进入工作台，无需重新登录。' : 'You are signed in. Access requires administrator approval. This page will open the workspace automatically after approval.')}</p>
    {error && <div className="alert alert-error" role="alert">{error}</div>}
    <button className="button button-primary" onClick={() => void reload()}>{zh ? '检查审批状态' : 'Check approval status'}</button>{' '}
    <button className="button button-ghost" onClick={() => void logout()}>{zh ? '退出登录' : 'Sign out'}</button>
  </section></div>
}

type AccessRequest = { user: User; status: 'pending' | 'approved' | 'rejected'; requestedAt: string; reviewedAt: string | null }

export function AccessApprovals({ locale }: { locale: Locale }) {
  const zh = locale === 'zh-CN'
  const [requests, setRequests] = useState<AccessRequest[]>([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const load = useCallback(async () => {
    try { const result = await api<{ requests: AccessRequest[] }>('/api/admin/access-requests'); setRequests(result.requests); setError('') }
    catch (error) { setError(error instanceof Error ? error.message : 'Request failed') }
  }, [])
  useEffect(() => { queueMicrotask(() => void load()); const timer = setInterval(() => void load(), 10000); return () => clearInterval(timer) }, [load])
  const review = async (id: string, decision: 'approved' | 'rejected') => {
    setBusy(id)
    try { await api(`/api/admin/access-requests/${encodeURIComponent(id)}/review`, { method: 'POST', body: jsonBody({ decision }) }); await load() }
    catch (error) { setError(error instanceof Error ? error.message : 'Request failed') }
    finally { setBusy(null) }
  }
  const labels = zh ? { pending: '待审批', approved: '已通过', rejected: '已拒绝' } : { pending: 'Pending', approved: 'Approved', rejected: 'Declined' }
  return <section className="content-section">
    <h2>{zh ? '用户接入审批' : 'User access requests'}</h2>
    <p>{zh ? '新用户登录后需在此审批。审批仅开放账号已有权限，不会额外授予管理员权限。' : 'Review new sign-ins here. Approval enables existing account permissions without granting additional administrator privileges.'}</p>
    {error && <div className="alert alert-error" role="alert">{error}</div>}
    {!requests.length && !error && <p>{zh ? '暂无接入申请' : 'No access requests'}</p>}
    {requests.map(request => <article className="ticket-row" key={request.user.id}>
      <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}><strong>{userLabel(request.user)}</strong><small>{request.user.telegram ? `Telegram ${request.user.telegram.id}${request.user.telegram.username ? ` · @${request.user.telegram.username}` : ''}` : request.user.email}</small><small>{new Date(request.requestedAt).toLocaleString(locale)} · {labels[request.status]}</small></div>
      {request.status === 'pending' && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="button button-primary" disabled={busy !== null} onClick={() => void review(request.user.id, 'approved')}>{zh ? '通过' : 'Approve'}</button>
        <button className="button button-ghost" disabled={busy !== null} onClick={() => void review(request.user.id, 'rejected')}>{zh ? '拒绝' : 'Decline'}</button>
      </div>}
    </article>)}
  </section>
}
